/**
 * Buddy group mutation hooks — update, add member, remove member, dissolve.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  buddyGroupAdminKeys,
  updateBuddyGroup,
  addBuddyGroupMember,
  removeBuddyGroupMember,
  dissolveBuddyGroup,
} from '../buddy-group-service';
import { buddyGroupOpsKeys } from '@/api/extensions/useBuddyGroupOps';
import type { UpdateBuddyGroupPayload, AddMemberPayload } from '../../types/buddy-group';

/** Invalidate both admin AND user-facing buddy group caches */
function invalidateBothSurfaces(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: buddyGroupAdminKeys.all });
  qc.invalidateQueries({ queryKey: buddyGroupOpsKeys.all });
}

export function useUpdateBuddyGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBuddyGroupPayload }) =>
      updateBuddyGroup(id, payload),
    onSuccess: () => invalidateBothSurfaces(qc),
  });
}

export function useAddBuddyGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, payload }: { groupId: string; payload: AddMemberPayload }) =>
      addBuddyGroupMember(groupId, payload),
    onSuccess: () => invalidateBothSurfaces(qc),
  });
}

export function useRemoveBuddyGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, memberProfileId }: { groupId: string; memberProfileId: string }) =>
      removeBuddyGroupMember(groupId, memberProfileId),
    onSuccess: () => invalidateBothSurfaces(qc),
  });
}

export function useDissolveBuddyGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dissolveBuddyGroup(id),
    onSuccess: () => invalidateBothSurfaces(qc),
  });
}
